import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, UserCircle, PlayCircle, Briefcase, SignOut } from '@phosphor-icons/react';
import axios from 'axios';
import { detectSubdomainArea } from '@/utils/detectSubdomain';
import { useAuth } from '@/contexts/AuthContext';

const API = "";

const ALL_AREAS = {
  my: {
    id: 'my',
    label: 'MYIHTP',
    tagline: 'O teu portal pessoal',
    description: 'Encomendas, comissões, dados, rede e downloads.',
    href: '/my',
    Icon: UserCircle,
    color: '#0F1829',
  },
  media: {
    id: 'media',
    label: 'Media Center',
    tagline: 'Conteúdos áudio e vídeo',
    description: 'Cursos, programas, formações e sessões.',
    href: '/media',
    Icon: PlayCircle,
    color: '#8B5CF6',
  },
  crm: {
    id: 'crm',
    label: 'CRM',
    tagline: 'Ferramenta de trabalho',
    description: 'Leads, negócios, encomendas e projeção.',
    href: '/',
    Icon: Briefcase,
    color: '#059669',
  },
};

const LAST_AREA_KEY = 'ihtp_last_area';

export default function AreaSelectorPage() {
  const navigate = useNavigate();
  const { logout: authLogout } = useAuth();
  const [user, setUser] = useState(null);
  const [areas, setAreas] = useState([]);
  const [loading, setLoading] = useState(true);

  // Resolve the current user + allowed areas. On reload we still need the info
  // (state is lost), so query /api/auth/me — if that 401s the user has only a
  // MY cookie and we fall back to /api/my/auth/me.
  //
  // CRM appears in the list when the user has Acesso_CRM='S' OR a valid CRM
  // session cookie is present. The MY login flow now mints a cross-area CRM
  // cookie automatically for consultores so transitioning from MY → CRM is
  // seamless and does not require a 2nd login.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const found = { user: null, areas: ['my', 'media'] };
      try {
        const me = await axios.get(`${API}/api/auth/me`, { withCredentials: true });
        if (me.data?.ID_Cliente) {
          found.user = me.data;
          found.areas = ['my', 'media', 'crm'];
        }
      } catch (e) {
        // 401/network on /auth/me means no CRM session — that's expected
        // for MY-only users. Log only unexpected failures.
        if (e?.response?.status && e.response.status !== 401) {
          console.warn('[AreaSelector] /auth/me failed unexpectedly:', e.response.status);
        }
      }
      if (!found.user) {
        try {
          const myMe = await axios.get(`${API}/api/my/auth/me`, { withCredentials: true });
          if (myMe.data?.ID_Cliente) {
            found.user = myMe.data;
            // User logged in via MY but has Acesso_CRM='S' — show the CRM area.
            // The cross-area cookie was already issued by /api/my/auth/login,
            // so clicking the CRM card will land on the CRM home directly.
            if (
              (myMe.data.Acesso_CRM || '').toUpperCase() === 'S' ||
              myMe.data.IsConsultor === true
            ) {
              found.areas = ['my', 'media', 'crm'];
            }
          }
        } catch (e) {
          if (e?.response?.status && e.response.status !== 401) {
            console.warn('[AreaSelector] /my/auth/me failed unexpectedly:', e.response.status);
          }
        }
      }
      if (cancelled) return;
      if (!found.user) {
        navigate('/login', { replace: true });
        return;
      }
      setUser(found.user);
      setAreas(found.areas);
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [navigate]);

  const subdomainHint = useMemo(() => detectSubdomainArea(), []);

  const visibleAreas = useMemo(() => {
    const list = areas.map((id) => ALL_AREAS[id]).filter(Boolean);
    if (!subdomainHint) return list;
    // Promote the subdomain-implied area to the front so it reads as the
    // "primary" choice when the user arrives via e.g. crm.ihavethepower.net.
    const idx = list.findIndex((a) => a.id === subdomainHint);
    if (idx <= 0) return list;
    const hinted = list[idx];
    return [hinted, ...list.slice(0, idx), ...list.slice(idx + 1)];
  }, [areas, subdomainHint]);

  const enter = (href, id) => {
    try { window.localStorage.setItem(LAST_AREA_KEY, id); } catch (e) {
      if (process.env.NODE_ENV !== "production") console.warn("AreaSelector: localStorage write failed", e);
    }
    window.location.href = href;
  };

  const handleLogout = async () => {
    // Delegate to AuthContext.logout so the CRM `user` state is cleared *before*
    // we navigate — otherwise /login sees `user` still truthy and bounces back
    // to /areas, causing a flicker loop.
    await authLogout();
    navigate('/login', { replace: true });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0F1829] flex items-center justify-center">
        <div className="h-1 w-32 bg-white/20 rounded-full overflow-hidden">
          <div className="h-full bg-white animate-pulse" style={{ width: '60%' }} />
        </div>
      </div>
    );
  }

  const firstName = (user?.NomeCompleto || user?.name || '').split(' ')[0];

  return (
    <div className="min-h-screen bg-[#0F1829] text-white flex flex-col" data-testid="area-selector-page"
      style={{ fontFamily: "'Manrope', sans-serif" }}>
      {/* Header */}
      <div className="px-6 sm:px-10 py-6 flex items-center justify-between border-b border-white/10">
        <div>
          <img src="/logo-ihtp.svg" alt="IHTP" className="h-7 w-auto mb-2" data-testid="area-brand-logo" />
          <p className="text-[11px] uppercase tracking-[0.25em] text-white/50">Suite</p>
        </div>
        <button onClick={handleLogout} data-testid="area-logout-btn"
          className="flex items-center gap-1.5 text-xs uppercase tracking-[0.15em] text-white/60 hover:text-white transition">
          <SignOut size={14} /> Sair
        </button>
      </div>

      {/* Welcome */}
      <div className="flex-1 flex flex-col items-center justify-center px-6 sm:px-10 py-10">
        <div className="w-full max-w-[1100px]">
          <div className="text-center mb-10 sm:mb-12">
            <p className="text-[11px] uppercase tracking-[0.3em] text-white/50 mb-2">Bem-vindo{firstName ? ',' : ''}</p>
            <h2 className="text-3xl sm:text-5xl font-bold tracking-tight" style={{ fontFamily: "'Fraunces', serif" }}>
              {firstName || 'Olá'}.
            </h2>
            <p className="text-white/70 mt-3 text-sm sm:text-base">Escolhe a área onde queres entrar.</p>
          </div>

          <div
            className={`grid gap-4 sm:gap-6 ${
              visibleAreas.length === 1 ? 'grid-cols-1 max-w-sm mx-auto'
              : visibleAreas.length === 2 ? 'grid-cols-1 sm:grid-cols-2'
              : 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3'
            }`}
            data-testid="area-cards"
          >
            {visibleAreas.map((a) => {
              const isHinted = subdomainHint === a.id;
              return (
                <button key={a.id} onClick={() => enter(a.href, a.id)} data-testid={`area-card-${a.id}`}
                  className={`group relative text-left bg-white/5 hover:bg-white/10 border rounded-2xl p-6 sm:p-7 transition-all duration-200 overflow-hidden ${
                    isHinted
                      ? 'border-white/60 ring-2 ring-white/30 shadow-[0_0_40px_-8px_rgba(255,255,255,0.25)]'
                      : 'border-white/10 hover:border-white/30'
                  }`}>
                  {isHinted && (
                    <span data-testid={`area-card-hint-${a.id}`}
                      className="absolute top-3 right-3 text-[9px] uppercase tracking-[0.2em] font-semibold px-2 py-1 rounded-full bg-white text-[#0F1829]">
                      Este subdomínio
                    </span>
                  )}
                  <div
                    className="absolute -top-10 -right-10 w-40 h-40 rounded-full opacity-10 group-hover:opacity-20 transition"
                    style={{ background: a.color }}
                  />
                  <div className="relative">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="p-2.5 rounded-xl" style={{ background: `${a.color}26` }}>
                        <a.Icon size={26} weight="duotone" style={{ color: a.color }} />
                      </div>
                      <div>
                        <p className="text-[10px] uppercase tracking-[0.22em] text-white/50">{a.tagline}</p>
                        <h3 className="text-xl sm:text-2xl font-bold" style={{ fontFamily: "'Fraunces', serif" }}>
                          {a.label}
                        </h3>
                      </div>
                    </div>
                    <p className="text-sm text-white/70 leading-relaxed mb-6 min-h-[40px]">{a.description}</p>
                    <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-semibold text-white/90 group-hover:text-white transition">
                      Entrar <ArrowRight size={14} className="group-hover:translate-x-1 transition" />
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      <div className="px-6 sm:px-10 py-5 text-center text-[10px] uppercase tracking-[0.25em] text-white/30">
        {user?.email}
      </div>
    </div>
  );
}
